import { Injectable } from '@angular/core';
import { apiResources} from "../../api/api.resources";
import {UserServiceInterface} from "./user.service.interface";
import {UserType, UserTypePayload} from "../../../modules/admin/general/users/user.type";
import {environment} from "../../../../environments/environment";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";

@Injectable({
    providedIn: 'root'
})
export class UserService implements UserServiceInterface{

    constructor(
        private _httpClient: HttpClient
    )
    {
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Get total users by roles
     */
    getTotalUsersByRoles() {
        return this._httpClient.get(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            apiResources.users.getTotalUsersByRoles);
    }



    /**
     * Get all users
     */
    getUsers(): Observable<UserType[]> {

        return this._httpClient.get<UserType[]>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base).pipe(
            map((response: any) => ({
                ...response,
                // PASS-THROUGH EXPLÍCITO do vínculo (`member`): o backend o
                // adicionou de forma aditiva em `GET /users`; qualquer
                // whitelist futura no service não pode descartá-lo.
                users: (response?.users ?? []).map((user: any) => this._mapUser(user)),
            }))
        );
    }



    /**
     * Normaliza o usuário preservando TODOS os campos e garantindo o vínculo
     * `member` ({id, fullName, ...} | null) exposto pelo backend.
     *
     * R2-04: `GET /users` devolve o array de roles com LINHAS DUPLICADAS para
     * alguns usuários (mesmo `id`/`guardName` repetido — artefato do join do
     * backend). Deduplicamos por `name` na normalização para que o card exiba o
     * badge do PERFIL (displayName) em vez de cair indevidamente em
     * "Multi Função" — perfis distintos (multi-role real) são preservados.
     */
    private _mapUser(user: any): any {
        return {
            ...user,
            member: user?.member ?? null,
            roles: this._dedupeRoles(user?.roles),
        };
    }



    /**
     * Remove roles repetidas por `name` preservando a ordem e o 1º objeto.
     */
    private _dedupeRoles(roles: any[]): any[] {
        if (!Array.isArray(roles)) {
            return [];
        }

        const seen = new Set<string>();
        return roles.filter((role: any) => {
            const key = role?.name;
            if (key === undefined || key === null || seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }



    /**
     * Update status user
     */
    updateStatusUser(id: any, status: any) {

        let response: any;
        let path = environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base + '/' + id +
            apiResources.users.status;

        response = this._httpClient.put(path, {status: status});

        return response;
    }



    /**
     * Save a user
     */
    saveUser(id: any, user: UserTypePayload) {

        const headers = new HttpHeaders();
        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');
        let body = user;
        let response: any;
        let path = environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base;

        if(id !== undefined){
            response = this._httpClient.put(path + '/' + id, body);
        }else{
            response = this._httpClient.post(path, body, {headers:headers});
        }

        return response;

    }



    /**
     * Get a user by id
     */
    getUserById(id: any): Observable<UserType> {

        return this._httpClient.get<UserType>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base + '/' + id).pipe(
            map((response: any) => ({
                ...response,
                user: response?.user ? this._mapUser(response.user) : response?.user,
            }))
        );
    }



    /**
     * Lookup server-side de membros para o seletor "Membro vinculado"
     * (`GET /users/members/lookup?term=` — term mínimo 2 caracteres).
     * Retorna `{members: [{id, fullName, avatar?}]}`.
     */
    lookupMembers(term: string): Observable<any> {

        const params = new HttpParams().set('term', term);

        return this._httpClient.get(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            apiResources.users.membersLookup,
            { params });
    }



    /**
     * Upload avatar user image
     */
    uploadAvatarUser(file: any): Observable<any> {

        const headers = new HttpHeaders();
        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');
        let response: any;
        let path = environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            apiResources.users.files.base +
            apiResources.users.files.assets.base +
            apiResources.users.files.assets.avatar;

        response = this._httpClient.post(path, file, {headers: headers});

        return response;
    }



    /**
     * Change user password
     */
    changePassword(currentPassword: string, newPassword: string): Observable<any> {

        const body = {
            currentPassword: currentPassword,
            newPassword: newPassword
        };

        let path = environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            apiResources.users.changePassword;

        return this._httpClient.put(path, body);
    }


    /**
     * Get users indicators (total, actives, inactives, logged)
     */
    getUsersIndicators(): Observable<any> {
        return this._httpClient.get(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            '/indicators');
    }

    /**
     * Delete a user by id
     */
    deleteUser(id: number): Observable<any> {
        return this._httpClient.delete<any>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            '/' + id
        );
    }

    /**
     * Get logged users list with counters
     */
    getLoggedUsersList(): Observable<any> {
        return this._httpClient.get(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.users.base +
            '/logged/list');
    }
}
